import { WordHelp } from "@/components/lesson/WordHelp";
import { useEffect, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { streamSSE } from "@/lib/api";
import { rehypeWordSpans, useReadAlong, wordIndexOf } from "@/lib/readAlong";
import { ReadAlongControls } from "./ReadAlongControls";
import { useProfile } from "@/context/ProfileContext";
import { SpanishPara, SpanishLi, SpanishH2, SpanishH3, BilingualCtx } from "./SpanishPara";
import { Languages } from "lucide-react";

export const LessonStep = ({ req, lesson, setLesson, locked, onDone }) => {
  const [loading, setLoading] = useState(false);
  const { buddy, profile } = useProfile();
  const [bi, setBi] = useState(() => sessionStorage.getItem("cl_bilingual") === "1");
  useEffect(() => { sessionStorage.setItem("cl_bilingual", bi ? "1" : "0"); }, [bi]);
  const who = buddy?.name || "Aria";
  const ra = useReadAlong();
  const article = useRef(null);
  const read = (from = 0) => ra.play(lesson, buddy?.voice, article.current, "lesson", from).catch(() => toast.error(`${who}'s voice is unavailable right now.`));
  const onWord = (e) => { if (profile?.spanish_help) return; const i = wordIndexOf(e); if (i != null && !loading && lesson) read(i); };
  const [error, setError] = useState("");
  const started = useRef(false);

  const load = async () => {
    ra.stop(); setLoading(true); setError(""); setLesson("");
    try {
      await streamSSE("/lessons/stream", req, (d) => setLesson((t) => t + d));
    } catch (e) {
      setError(`${who} couldn't prepare the lesson. Please try again.`);
    } finally { setLoading(false); }
  };

  useEffect(() => {
    if (started.current || lesson) return;
    started.current = true;
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div data-testid="lesson-step">
      {!lesson && loading && (
        <div className="py-16 text-center text-stone-500" data-testid="lesson-loading">
          <div className="w-14 h-14 mx-auto rounded-full bg-[#6B9080]/40 animate-pulse" />
          <p className="mt-4 font-display text-lg text-[#2C4A3E]">{who} is preparing your lesson…</p>
        </div>
      )}
      {error && <p className="text-red-700 bg-red-50 rounded-xl p-4" data-testid="lesson-error">{error}</p>}
      {lesson && <div className="mb-6"><ReadAlongControls ra={ra} who={who} onStart={() => read()} testId="lesson-narrate-top-btn" sticky big waiting={loading} /></div>}
      {lesson && !loading && <WordHelp containerRef={article} onOpen={ra.pause} />}
      {lesson && !loading && profile?.role === "student" && profile.spanish_help && (
        <button onClick={() => setBi(!bi)} aria-pressed={bi} data-testid="lesson-bilingual-toggle"
          className={`mb-5 inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm border transition-colors ${bi ? "bg-[#C87D53] border-[#C87D53] text-white" : "border-[#E6DEC3] text-[#2C4A3E] hover:bg-[#F1EADB]"}`}>
          <Languages className="w-4 h-4" />{bi ? "Bilingual on · English | Español" : "Bilingual"}
        </button>)}
      {bi && profile?.spanish_help && <div className="hidden md:grid grid-cols-2 gap-x-8 text-[10px] uppercase tracking-widest text-stone-500 mb-2 max-w-6xl" data-testid="bi-headers"><span>English</span><span className="pl-6">Español</span></div>}
      <BilingualCtx.Provider value={bi && !!profile?.spanish_help}>
      <article ref={article} onClick={onWord} className={`lesson-md ${bi && profile?.spanish_help ? "max-w-6xl" : "max-w-3xl"} text-stone-700 ${loading ? "cursor-blink" : ""}`} data-testid="lesson-content">
        <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={loading ? [] : [rehypeWordSpans]} components={loading ? undefined : { p: SpanishPara, li: SpanishLi, h2: SpanishH2, h3: SpanishH3 }}>{lesson}</ReactMarkdown>
      </article>
      </BilingualCtx.Provider>
      {!loading && (
        <div className="mt-10 flex flex-wrap gap-3">
          {lesson && (
            <button onClick={onDone} data-testid="lesson-continue-btn" className="px-6 py-3 rounded-full bg-[#2C4A3E] text-white hover:bg-[#1E352B] transition-colors">
              I'm ready to practice
            </button>
          )}
          {lesson && <ReadAlongControls ra={ra} who={who} onStart={() => read()} />}
          {!locked && <button onClick={load} data-testid="lesson-regenerate-btn" className="px-5 py-3 rounded-full border border-[#E6DEC3] text-stone-600 hover:bg-[#F1EADB] inline-flex items-center gap-2 transition-colors">
            <RefreshCw className="w-4 h-4" /> New version
          </button>}
        </div>
      )}
    </div>
  );
};
